import { z } from "zod";
import {
  OverAllTrackEnumValue,
  ScheduleAndBudgetTrend,
  ProjectDefaultViewEnumValue,
  ProjectStatusEnumValue,
  ZodErrorMessageEnumValue,
  UserRoleEnumValue,
  ProjectBudgetEnumValue,
} from "./enums.js";

export const optionalCoercedDateSchema = z.preprocess((val) => {
  if (val === "" || val === null || val === undefined) {
    return undefined;
  }
  return val;
}, z.coerce.date().optional());

const createProjectDateRefine = <S extends z.ZodRawShape>(schema: z.ZodObject<S>) =>
  schema.refine(
    (data: z.infer<z.ZodObject<S>>) => {
      const end = data.estimatedEndDate;
      const start = data.startDate;
      if (end && start) {
        return new Date(end as Date) >= new Date(start as Date);
      }
      return true;
    },
    {
      message: "End date precedes start date.",
      path: ["estimatedEndDate"],
    },
  );

export const createProjectSchema = createProjectDateRefine(
  z.object({
    projectName: z
      .string({
        required_error: ZodErrorMessageEnumValue.REQUIRED,
      })
      .trim()
      .min(1, { message: ZodErrorMessageEnumValue.REQUIRED })
      .max(250, { message: "character limit exceeded" }),
    projectDescription: z.string().max(10000).optional(),
    startDate: z.coerce.date({
      required_error: ZodErrorMessageEnumValue.REQUIRED,
    }),
    estimatedEndDate: optionalCoercedDateSchema,
    estimatedBudget: z.string().optional(),
    defaultView: z.nativeEnum(ProjectDefaultViewEnumValue, {
      required_error: ZodErrorMessageEnumValue.REQUIRED,
    }),
    currency: z.string({ required_error: ZodErrorMessageEnumValue.REQUIRED }),
    portfolio: z.preprocess(
      (val) => (val === undefined || val === null ? "" : String(val)),
      z.string(),
    ),
    program: z.preprocess(
      (val) => (val === undefined || val === null ? "" : String(val)),
      z.string(),
    ),
    isMilestone: z.boolean().optional(),
    projectCode: z.string().optional().nullable(),
    sponsor: z.string().optional().nullable(),
    clientDepartment: z.string().optional().nullable(),
    projectPhase: z.string().optional().nullable(),
    priority: z.string().optional().nullable(),
    projectType: z.string().optional().nullable(),
    projectCategory: z.string().optional().nullable(),
    tags: z.array(z.string()).optional().nullable(),
    includeInTimesheet: z.boolean().optional(),
    workstream: z.string().optional().nullable(),
    overallTrack: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(OverAllTrackEnumValue).optional().nullable()),
    budgetTrack: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(ProjectBudgetEnumValue).optional().nullable()),
    scheduleTrend: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(ScheduleAndBudgetTrend).optional().nullable()),
    budgetTrend: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(ScheduleAndBudgetTrend).optional().nullable()),
    documents: z
      .array(
        z.object({
          projectDocumentId: z.string().optional(),
          documentName: z.string(),
          documentUrl: z.string(),
        })
      )
      .optional()
      .nullable(),
  }),
);

export type CreateProjectPortfolioProgramClientContext = {
  isAdministrator: boolean;
  managedPortfolioCount: number;
  managedStandaloneProgramCount: number;
};

function hasNonEmptyId(value: string | undefined): boolean {
  return Boolean(value && String(value).trim().length > 0);
}

export function buildCreateProjectClientSchema(
  ctx: CreateProjectPortfolioProgramClientContext,
) {
  const {
    isAdministrator,
    managedPortfolioCount,
    managedStandaloneProgramCount,
  } = ctx;
  const hasManagedPortfolios = managedPortfolioCount > 0;
  const hasManagedStandalonePrograms = managedStandaloneProgramCount > 0;

  const baseFields = {
    projectName: z
      .string({
        required_error: ZodErrorMessageEnumValue.REQUIRED,
      })
      .trim()
      .min(1, { message: ZodErrorMessageEnumValue.REQUIRED })
      .max(250, { message: "character limit exceeded" }),
    projectDescription: z.string().max(10000).optional(),
    startDate: z.coerce.date({
      required_error: ZodErrorMessageEnumValue.REQUIRED,
    }),
    estimatedEndDate: optionalCoercedDateSchema,
    estimatedBudget: z.string().optional(),
    defaultView: z.nativeEnum(ProjectDefaultViewEnumValue, {
      required_error: ZodErrorMessageEnumValue.REQUIRED,
    }),
    currency: z.string({ required_error: ZodErrorMessageEnumValue.REQUIRED }),
    portfolio: z.string().optional(),
    program: z.string().optional(),
    isMilestone: z.boolean().optional(),
  };

  if (isAdministrator) {
    return createProjectDateRefine(z.object(baseFields));
  }

  if (hasManagedPortfolios && !hasManagedStandalonePrograms) {
    return createProjectDateRefine(
      z.object({
        ...baseFields,
        portfolio: z
          .string()
          .min(1, { message: ZodErrorMessageEnumValue.REQUIRED }),
        program: z.string().optional(),
      }),
    );
  }

  if (!hasManagedPortfolios && hasManagedStandalonePrograms) {
    return createProjectDateRefine(
      z.object({
        ...baseFields,
        portfolio: z.string().optional(),
        program: z
          .string()
          .min(1, { message: ZodErrorMessageEnumValue.REQUIRED }),
      }),
    );
  }

  if (hasManagedPortfolios && hasManagedStandalonePrograms) {
    return createProjectDateRefine(z.object(baseFields)).superRefine(
      (data, issue) => {
        if (
          !hasNonEmptyId(data.portfolio) &&
          !hasNonEmptyId(data.program)
        ) {
          issue.addIssue({
            code: z.ZodIssueCode.custom,
            message: ZodErrorMessageEnumValue.PORTFOLIO_OR_PROGRAM_REQUIRED,
            path: ["portfolio"],
          });
          issue.addIssue({
            code: z.ZodIssueCode.custom,
            message: ZodErrorMessageEnumValue.PORTFOLIO_OR_PROGRAM_REQUIRED,
            path: ["program"],
          });
        }
      },
    );
  }

  return createProjectDateRefine(z.object(baseFields));
}

export const updateProjectSchema = z
  .object({
    projectName: z.string().min(1).max(250, { message: "character limit exceeded" }).optional(),
    projectDescription: z.string().max(10000).optional(),
    startDate: optionalCoercedDateSchema,
    estimatedEndDate: optionalCoercedDateSchema,
    estimatedBudget: z.preprocess((val) => String(val), z.string().optional()),
    defaultView: z.nativeEnum(ProjectDefaultViewEnumValue).optional(),
    progressionPercentage: z.string().min(1).optional(),
    actualCost: z.string().min(1).optional(),
    budgetTrack: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(ProjectBudgetEnumValue).optional().nullable()),
    timeTrack: z.string().min(1).optional(),
    currency: z.string().min(1).optional(),
    overallTrack: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(OverAllTrackEnumValue).optional().nullable()),
    status: z.nativeEnum(ProjectStatusEnumValue).optional(),
    scheduleTrend: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(ScheduleAndBudgetTrend).optional().nullable()),
    budgetTrend: z.preprocess((val) => (val === "" ? null : val), z.nativeEnum(ScheduleAndBudgetTrend).optional().nullable()),
    consumedBudget: z.preprocess((val) => String(val), z.string().optional()),
    portfolio: z.string().optional().nullable(),
    program: z.string().optional().nullable(),
    isMilestone: z.boolean().optional(),
    setWorkloadToZero: z.boolean().optional(),
    projectCode: z.string().optional().nullable(),
    sponsor: z.string().optional().nullable(),
    clientDepartment: z.string().optional().nullable(),
    projectPhase: z.string().optional().nullable(),
    priority: z.string().optional().nullable(),
    projectType: z.string().optional().nullable(),
    projectCategory: z.string().optional().nullable(),
    tags: z.array(z.string()).optional().nullable(),
    includeInTimesheet: z.boolean().optional(),
    workstream: z.string().optional().nullable(),
    documents: z
      .array(
        z.object({
          projectDocumentId: z.string().optional(),
          documentName: z.string(),
          documentUrl: z.string(),
        })
      )
      .optional()
      .nullable(),
    ganttColor: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      if (data.estimatedEndDate) {
        return (
          new Date(data?.estimatedEndDate ?? new Date()) >=
          new Date(data.startDate ?? new Date())
        );
      }
      return true;
    },
    {
      message: "End date precedes start date.",
      path: ["estimatedEndDate"],
    }
  );

export const projectIdSchema = z.string().uuid();

export const projectStatusSchema = z.object({
  status: z.nativeEnum(ProjectStatusEnumValue),
});

export const createKanbanSchema = z
  .object({
    name: z.string({ required_error: ZodErrorMessageEnumValue.REQUIRED }),
    percentage: z.number().nullish(),
  })
  .refine((data) => {
    if (
      data.percentage !== undefined &&
      data.percentage &&
      data.percentage > 100
    ) {
      return [
        { field: "percentage", message: "Percentage should not exceed 100" },
      ];
    }
    return true;
  });

export const updateKanbanSchema = z
  .object({
    name: z.string({ required_error: ZodErrorMessageEnumValue.REQUIRED }),
    percentage: z.number().nullish(),
  })
  .refine((data) => {
    if (
      data.percentage !== undefined &&
      data.percentage &&
      data.percentage > 100
    ) {
      return [
        { field: "percentage", message: "Percentage should not exceed 100" },
      ];
    }
    return true;
  });

export const consumedBudgetSchema = z.object({
  consumedBudget: z.string({
    required_error: ZodErrorMessageEnumValue.REQUIRED,
  }),
});

export const projectAssginedRole = z.object({
  role: z.nativeEnum(UserRoleEnumValue),
});

export const assginedUserProjectSchema = z.object({
  assginedToUserId: z.string().uuid(),
});

export const addUserIntoProject = z
  .object({
    userRoleForProject: z.nativeEnum(UserRoleEnumValue),
    userId: z.string(),
  })
  .array();

export const projectJobTitleSchema = z.object({
  projectJobTitle: z.string(),
});
export const createPortfolioAndProgramSchema = z.object({
  portfolio: z.string().optional(),
  program: z.string().optional()
});